import { Button } from "@/src/components/buttons";
import { Card, CheckboxCard } from "@/src/components/cards";
import { openDrawer, closeDrawer } from "@/src/components/dialogs";
import { ListConstructor } from "@/src/components/fetchableLists";
import { ReorderableItems } from "@/src/components/reorderable";
import SearchKeyHighliter from "@/src/components/seachkeyHighlighter";
import { FolderItem, Exam, Patient } from "@/src/data/allTypes";
import { allItemTypes } from "@/src/data/data";
import apiFetch from "@/src/utils/apiFetch";
import { formatDate, getUserShortName, uid } from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { MdOutlineDragIndicator } from "react-icons/md";
import { openExamsVisualisationDrawer } from "../exams/openExamVisualisationDrawer";
import moment from "moment";

export function ReorderableHospitalisationExams({
    parent,
    currentValue,
    onChange,
  }: {
    parent: FolderItem;
    currentValue: Exam[];
    onChange: (newValue: Exam[]) => void;
  }) {
    let [items, setItems] = useState(currentValue);
  
    return (
      <div className="grid c1 gap10">
        <div className="grid c1 gap10">
          <ReorderableItems
            items={items || []}
            listId="exams"
            listType={"exams"}
            onReorder={(newItems) => {
              onChange(newItems);
              setItems(newItems);
            }}
          >
            {items?.map((exam) => {
              if (!exam) return null;
              let itemType = allItemTypes.exam;
              return (
                <Card
                  key={uid()}
                  color="orange"
                  title={exam.reference?.name}
                  leading={
                    <div className="grab disabled">
                      <MdOutlineDragIndicator size={15} />
                    </div>
                  }
                  subTitle={getUserShortName(exam?.creator)}
                  onClick={async () => {
                    if (!exam) return;
                    openExamsVisualisationDrawer({
                      exams: items,
                      index: items.indexOf(exam),
                      onEdit: async () => {
                        setItems([...items]);
                      },
                    });
                  }}
                  onDelete={() => {
                    if (!exam) return;
                    let newItems = [
                      ...items.filter((f) => f?.id != exam?.id && f != null),
                    ];
                    setItems(newItems);
                    onChange(newItems);
                  }}
                >
                  {formatDate(exam?._date, "LL HH:mm")}
                </Card>
              );
            })}
          </ReorderableItems>
        </div>
        <Button
          type={"tertiary"}
          onClick={async () => {
            let selectedExams: Exam[] = items || [];
            await openDrawer({
              id: "examsDrawer",
              title: "Examens",
              content: (
                <ExamsSelectionList
                  current={selectedExams}
                  patient={parent.patient!}
                  parent={parent}
                  onChange={(exams) => {
                    selectedExams = exams;
                  }}
                />
              ),
              onConfirm: () => {
                onChange(selectedExams);
                setItems(selectedExams);
                closeDrawer("examsDrawer");
              },
            });
          }}
        >
          Choisir un examen
        </Button>
      </div>
    );
  }
  
  function ExamsSelectionList({
    current,
    patient,
    parent,
    onChange,
  }: {
    current?: Exam[];
    patient: Patient;
    parent: FolderItem;
    onChange?: (selectedExams: Exam[]) => void | null | undefined;
  }) {
    const [reload, setReload] = useState(0);
    const [selectedExams, setSelectedExams] = useState(current || []);
    let [searchKey, setSearchKey] = useState("");
    let { data, isLoading, isError, error, refetch } = useQuery({
      queryKey: ["ExamsSelectionList", searchKey, patient.id, reload],
      queryFn: () =>
        apiFetch(
          "exams",
          "loadMany",
          {
            statuses: ["finished"],
            patientId: patient.id,
            parentId: parent?.id,
          },
          "Erreur lors du chargement des examens"
        ),
    });
    let exams = data?.exams || [];
  
    return (
      <ListConstructor
        data={exams}
        isLoading={isLoading}
        isError={isError}
        error={error}
      >
        <div className="grid c1 gap10">
          {exams.map((exam, index) => {
            let itemType = allItemTypes.exam;
            return (
              <CheckboxCard
                key={uid()}
                status={
                  selectedExams.find((f) => f?.id == exam?.id)
                    ? "active"
                    : "inactive"
                }
                color={exam.status != "finished" ? "simple" : "orange"}
                title={
                  <SearchKeyHighliter searchKey={searchKey}>
                    {exam.reference?.name}
                  </SearchKeyHighliter>
                }
                subTitle={getUserShortName(exam?.creator)}
                onEdit={async () => {
                  await openExamsVisualisationDrawer({
                    exams,
                    index,
                    onEdit: async () => {
                      setReload((prev) => prev + 1);
                    },
                  });
                }}
                onClick={async (e) => {
                  e.stopPropagation();
                  let fullExam = (await apiFetch(
                    "exams",
                    "loadById",
                    {
                      id: exam.id,
                    },
                    "Erreur lors du chargement de l'examen"
                  )) as Exam;
                  if (!fullExam) return;
                  let newSelectedExams = [...selectedExams];
                  if (selectedExams.find((f) => f?.id == fullExam?.id)) {
                    newSelectedExams = selectedExams.filter(
                      (f) => f?.id != fullExam?.id
                    );
                  } else {
                    newSelectedExams.push(fullExam);
                  }
                  setSelectedExams([...newSelectedExams]);
                  onChange?.(newSelectedExams);
                }}
              >
                <div className="c1 gap10">
                  <div className="gray">
                    {moment(exam?._date).format("LL HH:mm")}
                  </div>
                </div>
              </CheckboxCard>
            );
          })}
        </div>
      </ListConstructor>
    );
  }
  